import { useState } from "react";
import { Outlet, useLocation, useNavigate } from "react-router";
import { Sidebar } from "@/components/Sidebar";
import { Button } from "@mui/material";
import Add from "@mui/icons-material/Add";
import { Color } from "../lib/color";
import NotificationBell from "@/components/NotificationBell";
import "./Auth.css";

export default function AuthLayout() {
  const userSessionData = sessionStorage.getItem("data");
  const userSession = userSessionData ? JSON.parse(userSessionData) : { me: null };
  const isAdmin = userSession?.me?.usertype === "ADMIN";
  const navigate = useNavigate();
  const location = useLocation();
  const [activeLabel, setActiveLabel] = useState("Dashboard");

  // Page-specific navbar action buttons
  const pageActions: {
    pathMatch: string;
    label: string;
    route: string;
    adminOnly: boolean;
    partnerOnly?: boolean;
    exactMatch?: boolean;
  }[] = [
    {
      pathMatch: "/app/2/storageunit",
      label: "Add Storage Unit",
      route: "/app/2/storageunit/new",
      adminOnly: true,
      exactMatch: true,
    },
    // "Add Facility" removed for user login — admin-only action
    {
      pathMatch: "/app/1/users",
      label: "Add User",
      route: "/app/1/users/new",
      adminOnly: true,
      exactMatch: true,
    },
    {
      pathMatch: "/app/partner/customers",
      label: "Add Customers",
      route: "/app/partner/customers/new",
      adminOnly: false,
      partnerOnly: true,
      exactMatch: true,
    },
    {
      pathMatch: "/app/partner/inventory",
      label: "Add Stock",
      route: "/app/partner/inventory/new",
      adminOnly: false,
      partnerOnly: true,
      exactMatch: true,
    },
    {
      pathMatch: "/app/4/external-user",
      label: "Add External User",
      route: "/app/4/external-user/new",
      adminOnly: true,
      exactMatch: true,
    },
    {
      pathMatch: "/app/admin/partners",
      label: "Add Partner",
      route: "/app/admin/partners/add",
      adminOnly: true,
      exactMatch: true,
    },
    {
      pathMatch: "/app/partner/operators",
      label: "Add Operator",
      route: "/app/partner/operators/create",
      adminOnly: false,
      partnerOnly: true,
      exactMatch: true,
    },
  ];

  const currentPageAction = pageActions.find(
    (action) =>
      (action.exactMatch
        ? location.pathname === action.pathMatch
        : location.pathname.startsWith(action.pathMatch)) &&
      (!action.adminOnly || isAdmin) &&
      (!action.partnerOnly || !isAdmin)
  );

  // Map active label to page heading
  const getPageHeading = () => {
    if (activeLabel === "User" || activeLabel === "Users") return "User Management";
    if (activeLabel === "Customers") return "Customer Management";
    return activeLabel;
  };

  return (
    <div className="auth-layout">
      <Sidebar onActiveChange={setActiveLabel} />

      <div className="auth-main">
        {/* Top Header Bar */}
        <header className="auth-header">
          <h1 className="auth-page-title">{getPageHeading()}</h1>

          <div style={{ display: "flex", alignItems: "center" }}>
            {currentPageAction && (
              <Button
                variant="contained"
                size="medium"
                startIcon={<Add />}
                onClick={() => navigate(currentPageAction.route)}
                sx={{
                  textTransform: "none",
                  bgcolor: Color.color,
                  fontWeight: 600,
                  borderRadius: "8px",
                  px: 2.5,
                  py: 1,
                  fontSize: "14px",
                  boxShadow: "none",
                  "&:hover": {
                    bgcolor: Color.hover,
                    boxShadow: "none",
                  },
                }}
              >
                {currentPageAction.label}
              </Button>
            )}
            <NotificationBell />
          </div>
        </header>

        {/* Page Content */}
        <div className="auth-content">
          <Outlet />
        </div>
      </div>
    </div>
  );
}
